import {
  useEffect, useRef, useState,
} from 'react';
import { FiCreditCard, FiLock, FiAlertCircle } from 'react-icons/fi';
import Button from './atoms/Button';
import LoadingLottie from './atoms/LoadingLottie';
import Input from './atoms/Input';
import Select from './atoms/Select/Select';
import Checkbox from './atoms/Checkbox';
import { getStatusColor } from '../utils/theme-utils';
import { translatePayWayErrors } from '../utils/payway-error-translator';
import { PayWayTokenData } from '../store/apis/orders';
import { useLazyGetBinDataQuery } from '../store';
import type { BinData } from '../store';

// PayWay SDK Types based on official SDK
declare global {
  interface Window {
    Decidir?: new (url: string, disableCybersource?: boolean) => DecidirInstance;
  }
}

interface DecidirInstance {
  setPublishableKey: (key: string) => void;
  setTimeout: (ms: number) => void;
  createToken: (form: HTMLFormElement, callback: (status: number, response: DecidirResponse) => void) => void;
}

interface DecidirResponse {
  id?: string;
  bin?: string;
  last_four_digits?: string;
  expiration_month?: string;
  expiration_year?: string;
  card_holder?: {
    name: string;
    identification: {
      type: string;
      number: string;
    };
  };
  error?: Array<{
    error: {
      type: string;
      message: string;
      param: string;
    };
  }>;
}

interface PayWayCheckoutProps {
  amount: number;
  currency?: string;
  onTokenCreated: (tokenData: PayWayTokenData) => void;
  onError: (error: string) => void;
  onProcessing?: (isProcessing: boolean) => void;
  disabled?: boolean;
  buttonText?: string;
  orderItems?: Array<{
    name: string;
    last_name: string;
    person_id: string;
  }>;
}

const PayWayCheckout: React.FC<PayWayCheckoutProps> = ({
  amount,
  currency = 'ARS',
  onTokenCreated,
  onError,
  onProcessing,
  disabled = false,
  buttonText = 'Pagar con tarjeta',
  orderItems = [],
}) => {
  const [isSDKLoaded, setIsSDKLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [docType, setDocType] = useState<string>('dni');
  const [cardNumber, setCardNumber] = useState<string>('');
  const [binData, setBinData] = useState<BinData | null>(null);
  const [useOrderData, setUseOrderData] = useState(false);
  const [cardHolderName, setCardHolderName] = useState<string>('');
  const [docNumber, setDocNumber] = useState<string>('');

  const [fetchBinData] = useLazyGetBinDataQuery();

  const decidirRef = useRef<DecidirInstance | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const hiddenSelectRef = useRef<HTMLSelectElement>(null);

  // SDK URLs from official documentation
  //   const SANDBOX_URL = 'https://developers.decidir.com/api/v2';
  const PRODUCTION_URL = 'https://live.decidir.com/api/v2';

  const publicApiKey = 'c57c15d6514a42a490cf773459a6a14b';

  // Load PayWay SDK script
  useEffect(() => {
    const loadScript = () => {
      const sdkUrl = 'https://live.decidir.com/static/v2.5/decidir.js';

      console.log('[PayWay] Loading SDK from:', sdkUrl);
      console.log('[PayWay] Public API Key:', publicApiKey || 'NOT SET');

      if (window.Decidir) {
        console.log('[PayWay] SDK already loaded');
        setIsSDKLoaded(true);
        setIsLoading(false);
        return;
      }

      const existingScript = document.getElementById('payway-sdk');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          setIsSDKLoaded(true);
          setIsLoading(false);
        });
        return;
      }

      const script = document.createElement('script');
      script.id = 'payway-sdk';
      script.src = sdkUrl;
      script.async = true;

      script.onload = () => {
        console.log('[PayWay] SDK loaded successfully');
        setIsSDKLoaded(true);
        setIsLoading(false);
      };

      script.onerror = () => {
        console.error('[PayWay] Failed to load SDK');
        setIsLoading(false);
        onError('Error al cargar el sistema de pagos. Por favor, recargá la página.');
      };

      document.head.appendChild(script);
    };

    loadScript();
  }, [onError, publicApiKey]);

  // Initialize Decidir instance following official SDK documentation
  useEffect(() => {
    if (isSDKLoaded && window.Decidir && publicApiKey) {
      // Following official SDK: new Decidir(url, inhabilitarCS)
      decidirRef.current = new window.Decidir(PRODUCTION_URL, true);
      decidirRef.current.setPublishableKey(publicApiKey);
      decidirRef.current.setTimeout(10000); // 10 second timeout

      console.log('[PayWay] Decidir instance created with key:', `${publicApiKey.slice(0, 10)}...`);
    } else if (isSDKLoaded && !publicApiKey) {
      console.error('[PayWay] No public API key provided');
    }
  }, [isSDKLoaded, publicApiKey]);

  // Fetch BIN data when card number has 8+ digits
  useEffect(() => {
    const cleanCardNumber = cardNumber.replace(/\s/g, '');
    if (cleanCardNumber.length >= 8) {
      const bin = cleanCardNumber.substring(0, 8);
      fetchBinData({ bin })
        .unwrap()
        .then((data) => {
          if (data) {
            console.log('[PayWay] BIN data fetched:', data);
            setBinData(data);
          }
        })
        .catch((err) => {
          console.warn('[PayWay] Failed to fetch BIN data:', err);
          // Don't show error to user, fallback to card brand detection
          setBinData(null);
        });
    } else {
      setBinData(null);
    }
  }, [cardNumber, fetchBinData]);

  useEffect(() => {
    if (useOrderData && orderItems.length > 0) {
      const firstItem = orderItems[0];
      const fullName = `${firstItem.name} ${firstItem.last_name}`.trim().toUpperCase();
      setCardHolderName(fullName);
      setDocNumber(firstItem.person_id || '');

      setTimeout(() => {
        if (formRef.current) {
          const nameInput = formRef.current.querySelector('#card_holder_name') as HTMLInputElement;
          const docInput = formRef.current.querySelector('#card_holder_doc_number') as HTMLInputElement;
          if (nameInput) {
            nameInput.value = fullName;
            const nameEvent = new Event('input', { bubbles: true });
            nameInput.dispatchEvent(nameEvent);
          }
          if (docInput) {
            docInput.value = firstItem.person_id || '';
            const docEvent = new Event('input', { bubbles: true });
            docInput.dispatchEvent(docEvent);
          }
        }
      }, 0);
    } else if (!useOrderData) {
      setCardHolderName('');
      setDocNumber('');
      setTimeout(() => {
        if (formRef.current) {
          const nameInput = formRef.current.querySelector('#card_holder_name') as HTMLInputElement;
          const docInput = formRef.current.querySelector('#card_holder_doc_number') as HTMLInputElement;
          if (nameInput) {
            nameInput.value = '';
            const nameEvent = new Event('input', { bubbles: true });
            nameInput.dispatchEvent(nameEvent);
          }
          if (docInput) {
            docInput.value = '';
            const docEvent = new Event('input', { bubbles: true });
            docInput.dispatchEvent(docEvent);
          }
        }
      }, 0);
    }
  }, [useOrderData, orderItems]);

  // Handle form submission
  const handleSubmit = () => {
    if (!formRef.current) {
      onError('Error interno del formulario');
      return;
    }

    if (!decidirRef.current) {
      onError('El SDK de PayWay no está inicializado. Verificá la API key.');
      return;
    }

    setIsProcessing(true);
    onProcessing?.(true);

    console.log('[PayWay] Creating token...');

    // Following official SDK: decidir.createToken(form, callback)
    decidirRef.current.createToken(formRef.current, (status, response) => {
      console.log('[PayWay] Response:', { status, response });

      setIsProcessing(false);
      onProcessing?.(false);

      if (status === 200 || status === 201) {
        if (response.id) {
          const bin = response.bin || '';
          // Use payway_code from BIN API if available, otherwise fallback to card brand detection
          const paymentMethodId = binData?.payway_code ?? 0;
          const tokenData: PayWayTokenData = {
            token: response.id,
            bin,
            payment_method_id: paymentMethodId,
            installments: 1,
          };
          console.log('[PayWay] Token created:', tokenData);
          console.log('[PayWay] Payment method ID source:', binData?.payway_code ? 'BIN API' : 'Card brand detection');
          onTokenCreated(tokenData);
        } else {
          onError('No se pudo obtener el token de pago');
        }
      } else {
        // Handle error response - translate PayWay errors to Spanish
        const errorMsg = translatePayWayErrors(response.error);
        console.error('[PayWay] Error:', response.error);
        onError(errorMsg);
      }
    });
  };

  const errorStyles = getStatusColor('error');

  if (isLoading) {
    return (
      <div className="glass-surface rounded-3xl p-6 flex flex-col items-center justify-center min-h-[300px]">
        <LoadingLottie />
        <p className="text-text-secondary mt-4">Cargando sistema de pagos...</p>
      </div>
    );
  }

  if (!isSDKLoaded) {
    return (
      <div className={`glass-surface rounded-3xl p-6 flex flex-col items-center justify-center min-h-[200px] ${errorStyles.bg}`}>
        <FiAlertCircle className={`text-4xl ${errorStyles.icon} mb-4`} />
        <p className={errorStyles.text}>No se pudo cargar el sistema de pagos</p>
        <Button
          variant="secondary"
          onClick={() => window.location.reload()}
          className="mt-4"
        >
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="glass-surface w-full lg:p-3">
      <div className="flex items-center gap-3 mb-6">
        <FiCreditCard className="text-2xl text-primary-500" />
        <h2 className="text-lg font-semibold text-text-primary">Datos de la tarjeta</h2>
        <div className="ml-auto flex items-center gap-1 text-text-tertiary text-sm">
          <FiLock className="text-success-500" />
          <span>Pago seguro</span>
        </div>
      </div>

      {/* Form with data-decidir attributes as per official SDK documentation */}
      <form ref={formRef} className="flex flex-col gap-4">
        {orderItems.length > 0 && (
          <div className="mt-2">
            <Checkbox
              id="use-order-data"
              label="Usar datos de la orden"
              isChecked={useOrderData}
              onCheckChange={setUseOrderData}
              disabled={disabled || isProcessing}
              labelClassName="text-sm text-text-primary"
            />
            {orderItems.length > 0 && (
              <p className="ml-8 mt-1 text-xs text-text-secondary">
                Se completarán automáticamente: <span className="font-medium text-text-primary">
                  {orderItems[0].name} {orderItems[0].last_name}
                </span> (DNI: {orderItems[0].person_id})
              </p>
            )}
          </div>
        )}
        {/* Card Number */}
        <Input
          id="card_number"
          label="Número de tarjeta"
          type="text"
          inputMode="numeric"
          autoComplete="cc-number"
          data-decidir="card_number"
          placeholder="4509 9535 6623 3704"
          disabled={disabled || isProcessing}
          labelAbove
          value={cardNumber}
          onChange={(e) => setCardNumber(e.target.value)}
        />

        {/* Expiration & CVV Row */}
        <div className="grid grid-cols-3 gap-3">
          <Input
            id="card_expiration_month"
            label="Mes"
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp-month"
            data-decidir="card_expiration_month"
            placeholder="08"
            maxLength={2}
            disabled={disabled || isProcessing}
            labelAbove
          />

          <Input
            id="card_expiration_year"
            label="Año"
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp-year"
            data-decidir="card_expiration_year"
            placeholder="30"
            maxLength={2}
            disabled={disabled || isProcessing}
            labelAbove
          />

          <Input
            id="security_code"
            label="CVV"
            type="text"
            inputMode="numeric"
            autoComplete="cc-csc"
            data-decidir="security_code"
            placeholder="123"
            maxLength={4}
            disabled={disabled || isProcessing}
            labelAbove
          />
        </div>

        {/* Card Holder Name */}
        <Input
          id="card_holder_name"
          label="Nombre del titular"
          type="text"
          autoComplete="cc-name"
          data-decidir="card_holder_name"
          placeholder="NOMBRE APELLIDO"
          disabled={disabled || isProcessing}
          labelAbove
          value={cardHolderName}
          onChange={(e) => setCardHolderName(e.target.value)}
        />

        {/* Document Type & Number */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            {/* Hidden native select for PayWay SDK compatibility */}
            <select
              ref={hiddenSelectRef}
              id="card_holder_doc_type"
              data-decidir="card_holder_doc_type"
              value={docType}
              disabled={disabled || isProcessing}
              className="hidden"
              aria-hidden="true"
            >
              <option value="dni">DNI</option>
              <option value="cuit">CUIT</option>
              <option value="cuil">CUIL</option>
            </select>
            <Select
              id="card_holder_doc_type_ui"
              label="Tipo Doc."
              options={[
                { label: 'DNI', value: 'dni' },
                { label: 'CUIT', value: 'cuit' },
                { label: 'CUIL', value: 'cuil' },
              ]}
              value={docType}
              disabled={disabled || isProcessing}
              onChange={(e) => {
                const newValue = e.target.value;
                setDocType(newValue);
                if (hiddenSelectRef.current) {
                  hiddenSelectRef.current.value = newValue;
                }
              }}
            />
          </div>

          <div className="col-span-2">
            <Input
              id="card_holder_doc_number"
              label="Número de documento"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              data-decidir="card_holder_doc_number"
              placeholder="45981091"
              disabled={disabled || isProcessing}
              labelAbove
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
            />
          </div>
        </div>

        {/* Amount Display */}
        <div className="flex items-center justify-between p-4 bg-surface-secondary rounded-xl mt-2">
          <span className="text-text-secondary">Total a pagar</span>
          <span className="text-xl font-bold text-text-primary">
            {currency === 'ARS' ? '$' : currency}
            {' '}
            {(amount / 100).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
          </span>
        </div>

        {/* Submit Button */}
        <Button
          type="button"
          variant="primary"
          disabled={disabled || isProcessing}
          loading={isProcessing}
          className="mt-2"
          onClick={handleSubmit}
        >
          <div className="flex items-center gap-2 justify-center">
            <FiLock />
            <span>{isProcessing ? 'Procesando...' : buttonText}</span>
          </div>
        </Button>

        {/* Security Notice */}
        <p className="text-xs text-text-tertiary text-center mt-2">
          Tus datos están protegidos con encriptación de nivel bancario.
          <br />
          Procesado de forma segura por PayWay/Decidir.
        </p>
      </form>
    </div>
  );
};

export default PayWayCheckout;
